import axios from "../../api/axios";
import { toast } from 'react-toastify';

/**
 * API endpoints for inventory operations
 */
export const ENDPOINTS = {
  GET_PROPERTIES: "v2/master_sheet/property-list",
  GET_INVENTORY: "v2/inventory",
  CREATE_INVENTORY: "v2/inventory/",
  CREATE_INVENTORY_ITEM: "v2/inventory/",
  UPDATE_INVENTORY_ITEM: "v2/inventory/:id",
  DELETE_INVENTORY_ITEM: "v2/inventory/:id",
  CHECK_ITEM_EXISTS: "v2/inventory/check-item-exists",
  CREATE_ORDER: "v2/inventory/create-order",
  APPEND_ITEM_QUANTITY: "v2/inventory/append-inventory-item-quantity",

  GET_SIGNED_URL: "v2/inventory/get-signed-url",
  UPLOAD_DOCUMENT: "v2/inventory/upload-documents",

  GET_REQUEST_TYPE: "requesttype/getrequest",
};

/**
 * Get a signed URL for a document.
 * @param {Object} params - Parameters object.
 * @param {string} params.key - The key for the document.
 * @returns {Promise<Object>} - The signed URL response object.
 */
export const getSignedUrl = async ({ key }) => {
  const {
    data: { data: response },
  } = await axios.get(ENDPOINTS.GET_SIGNED_URL, {
    params: { key },
  });
  return response;
};

/**
 * Upload requested documents.
 * @param {FormData} formData - The form data containing files.
 * @param {function} onUploadProgress - Optional progress callback.
 * @returns {Promise<Object>} - The upload response data.
 */
export const uploadDocument = async (property_id, formData, onUploadProgress) => {
  const { data } = await axios.post(
    `${ENDPOINTS.UPLOAD_DOCUMENT}?property_id=${property_id}`,
    formData,
    {
      onUploadProgress,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return data;
};

/**
 * Centralized error handler for API calls
 * @param {Error} error - Axios error object
 * @param {string} defaultMessage - Default error message
 */
const handleApiError = (error, defaultMessage) => {
  const message = error.response?.data?.message || error.message || defaultMessage;
  console.error(defaultMessage, error);
  toast.error(message); // Display error via toast
  throw new Error(message);
};

/**
 * Fetches property list based on user role and ID
 * @param {string} role - User role
 * @param {string} id - User ID
 * @returns {Promise<Object>} Filtered property data
 */
export const getPropertyList = async (role, id) => {
  try {
    const { data } = await axios.get(
      `${ENDPOINTS.GET_PROPERTIES}?role=${role || ""}&id=${id || ""}`
    );
    const properties = data.data || [];
    return {
      propertyList: properties,
      building_list: properties.length ? properties[0].building || [] : [],
    };
  } catch (error) {
    handleApiError(error, "Failed to fetch property list");
  }
};

/**
 * Check inventory for property and building
 * @param {string} property_id - Property ID
 * @param {string} building_name - Building name
 * @returns {Promise<Object>} Inventory and items
 */
export const getInventory = async (property_id, building_name) => {
  try {
    const response = await axios.get(ENDPOINTS.GET_INVENTORY, {
      params: { property_id, building_name },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch inventory");
  }
};

/**
 * Create a new inventory
 * @param {Object} payload - Inventory data
 * @returns {Promise<Object>} Created inventory
 */
export const createInventory = async (payload) => {
  try {
    const response = await axios.post(ENDPOINTS.CREATE_INVENTORY, payload);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to create inventory");
  }
};

/**
 * Create a new inventory item
 * @param {Object} payload - Inventory item data
 * @returns {Promise<Object>} Created inventory item
 */
export const createInventoryItem = async (payload) => {
  try {
    const normalizedPayload = {
      ...payload,
      sku: Array.isArray(payload.sku) ? payload.sku.map(String).filter(Boolean) : [String(payload.sku)].filter(Boolean),
      quantity: Number(payload.quantity) || 0,
      min_quantity: Number(payload.min_quantity) || 0,
      max_quantity: Number(payload.max_quantity) || 0,
      cost_per_unit: Number(payload.cost_per_unit) || 0,
    };
    const response = await axios.post(ENDPOINTS.CREATE_INVENTORY_ITEM, normalizedPayload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    const message = error.response?.data?.message || "Failed to create inventory item";
    console.error("Error creating inventory item:", error);
    throw new Error(message);
  }
};

/**
 * Update an inventory item
 * @param {Object} values - Object containing an array of items to update
 * @returns {Promise<Object>} Updated inventory item
 */
export const updateItem = async (values) => {
  try {
    const url = ENDPOINTS.UPDATE_INVENTORY_ITEM.replace(':id', values.items[0]._id);
    const response = await axios.put(url, values.items[0]);
    return response.data;
  } catch (error) {
    const message = error.response?.data?.message || "Failed to update inventory item";
    console.error("Error updating inventory item:", error);
    throw new Error(message);
  }
};

/**
 * Delete an inventory item
 * @param {string} id - Item ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteItem = async (id) => {
  try {
    const url = ENDPOINTS.DELETE_INVENTORY_ITEM.replace(':id', id);
    const response = await axios.delete(url);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to delete inventory item");
  }
};

/**
 * Check if an item exists by SKU
 * @param {string} property_id - Property ID
 * @param {string} building_name - Building name
 * @param {string} sku - SKU to check
 * @returns {Promise<Object>} Existence check result
 */
export const checkItemExists = async (property_id, building_name, sku) => {
  try {
    const response = await axios.get(ENDPOINTS.CHECK_ITEM_EXISTS, {
      params: { property_id, building_name, sku },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to check item existence");
  }
};

/**
 * Create an order for an inventory item
 * @param {Object} payload - Order data
 * @returns {Promise<Object>} Created order
 */
export const createOrder = async (payload) => {
  try {
    const response = await axios.post(ENDPOINTS.CREATE_ORDER, payload);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to create order");
  }
};

/**
 * Append quantity to an existing inventory item
 * @param {Object} payload - Contains inventory_item_id and quantity
 * @returns {Promise<Object>} Updated inventory item
 */
export const appendInventoryItemQuantity = async (payload) => {
  try {
    const normalizedPayload = {
      ...payload,
      quantity: Number(payload.quantity) || 0,
    };
    const response = await axios.post(ENDPOINTS.APPEND_ITEM_QUANTITY, normalizedPayload);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to append item quantity");
  }
};

/**
 * Bulk create or update inventory items
 * @param {Object} payload - Contains property_id, building_name, and items array
 * @returns {Promise<Object>} Updated inventory and items
 */
export const bulkCreateOrUpdateItems = async (payload) => {
  try {
    const { property_id, building_name, items } = payload;

    // Check if inventory exists, create if not
    let inventory = await getInventory(property_id, building_name);
    let inventoryId = inventory?.inventory?._id;

    if (!inventoryId) {
      const inventoryPayload = { property_id, building_name };
      const inventoryResponse = await createInventory(inventoryPayload);
      inventoryId = inventoryResponse.inventory._id;
    }

    // Process each item
    const results = [];
    for (const item of items) {
      const { sku, quantity, cost_per_unit, ...rest } = item;
      const exists = await checkItemExists(property_id, building_name, sku);

      if (exists.exists) {
        // Append quantity to existing item
        const existingItem = inventory.items?.find((i) => i.sku.includes(sku));
        if (existingItem) {
          const appendPayload = {
            inventory_item_id: existingItem._id,
            quantity: Number(quantity) || 0,
          };
          const result = await appendInventoryItemQuantity(appendPayload);
          results.push(result);
        } else {
          throw new Error(`Item with SKU ${sku} found but not in inventory`);
        }
      } else {
        // Create new item
        const itemPayload = {
          ...rest,
          sku: Array.isArray(sku) ? sku.map(String).filter(Boolean) : [String(sku)].filter(Boolean),
          inventory_id: inventoryId,
          property_id,
          building_name,
          quantity: Number(quantity) || 0,
          cost_per_unit: Number(cost_per_unit) || 0,
        };
        const result = await createInventoryItem(itemPayload);
        results.push(result);
      }
    }

    // Fetch updated inventory
    const updatedInventory = await getInventory(property_id, building_name);
    return {
      inventory: updatedInventory.inventory,
      items: updatedInventory.items,
    };
  } catch (error) {
    handleApiError(error, "Failed to process bulk inventory items");
  }
};

/**
 * Fetch request types for a company.
 * @param {string} company_id - Company ID of the user.
 * @returns {Promise<Object>} Request types data.
 */
export const getRequestTypes = async (company_id) => {
  try {
    const response = await axios.post(ENDPOINTS.GET_REQUEST_TYPE, { company_id: company_id });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch request types");
  }
};
